<script>
import { mapState } from "vuex";
import { FEATURE_BROWSE_REPOSITORIES, FEATURE_MY_DOCUMENT_RETENTION } from "@/util/featureFlags";
import { callAPI } from "../../mixins/api";

const AUTH = {
  USER: 0,
  ADMIN: 1,
  REDS: 2,
  PERSONAL_STORAGE: 3,
  NONE: 3,
  MULTI_FILE_DOWNLOAD: 4
};

const GROUPS = [
  {
    items: [
      { label: 'browse-repositories', url: '/browse-repositories', auth: AUTH.USER, icon: "travel_explore" },
      { label: 'repositories',     url: '/repositories',     auth: AUTH.USER, icon: "account_circle" },
      { label: 'shared',           url: '/shared',           auth: AUTH.USER, icon: "folder_shared" },
      { label: 'personal-storage', url: '/personal-storage', auth: AUTH.PERSONAL_STORAGE, icon: "storage" },
      { label: 'uploads-my',       url: '/uploads',          auth: AUTH.USER, icon: "cloud_upload" },
      { label: 'downloads',        url: '/downloads',        auth: AUTH.MULTI_FILE_DOWNLOAD, icon: "cloud_download" },
    ],
  },
  {
    items: [
      { label: 'my-expired-items', url: '/my-expired-items', auth: AUTH.USER, icon: "edit_calendar", feature: 'MY_DOC_RETENTION' },
      { label: 'archive-requests', url: '/archive-requests', auth: AUTH.USER, icon: "assignment" },
    ],
  },
  {
    items: [
      { label: 'teams',            url: '/teams',            auth: AUTH.USER, icon: "groups" },
      { label: 'reds-search',      url: '/reds-search',      auth: AUTH.REDS, icon: "architecture" },
    ],
  },
  {
    items: [
      {
        label: 'navigation.administration',
        icon: 'admin_panel_settings',
        url: '/admin',
        auth: AUTH.ADMIN,
      },
      {
        label: 'jobs',
        icon: 'work',
        url: '/admin/jobs',
        auth: AUTH.ADMIN,
      },
      {
        label: 'navigation.reporting',
        icon: 'insights',
        url: '/admin/reporting',
        auth: AUTH.ADMIN,
      },
    ],
  },
];

const BOTTOM_ITEMS = [
  { label: 'trash', url: '/trash', auth: AUTH.USER, icon: "delete" },
];

export default {
  data() {
    return {
      appVersion: "",
      badgeSummary: {
        activeUploadCount: 0,
        activeDownloadCount: 0,
        recoverableTrashRootCount: 0,
      },
      badgeRefreshInterval: null,
    };
  },
  async created() {
    try {
      const response = await fetch("/version.txt");
      if (response.ok) {
        this.appVersion = await response.text();
      } else {
        console.error("Failed to fetch version.txt:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching version.txt:", error);
    }
  },
  mounted() {
    if (this.isAuthenticated) {
      this.startBadgePolling();
    }
  },
  beforeDestroy() {
    this.stopBadgePolling();
  },
  computed: {
    ...mapState({
      isAdmin: (state) => state.auth.userData?.isAdmin ?? false,
      hasPersonalStorage: (state) => state.auth.userData?.personalStorageFolder ?? false,
      isRedsUser: (state) => {
        let ud = state.auth.userData;
        return (ud?.roles.includes('REDS') || ud?.isAdmin) ?? false
      },
      hasDownloads: (state) => state.auth.userData?.personalStorageFolder ?? false,
      isAuthenticated: (state) => Boolean(state.auth.userData),
    }),
    visibleGroups() {
      return GROUPS
        .map((group) => ({
          items: group.items
            .filter((item) => this.isItemVisible(item))
            .map((item) => this.withBadge(item)),
        }))
        .filter((group) => group.items.length > 0);
    },
    visibleBottomItems() {
      return BOTTOM_ITEMS
        .filter((item) => this.isItemVisible(item))
        .map((item) => this.withBadge(item));
    },
  },
  methods: {
    startBadgePolling() {
      if (this.badgeRefreshInterval) {
        return;
      }
      this.loadBadgeSummary();
      this.badgeRefreshInterval = window.setInterval(() => this.loadBadgeSummary(), 30000);
    },
    stopBadgePolling() {
      if (this.badgeRefreshInterval) {
        window.clearInterval(this.badgeRefreshInterval);
        this.badgeRefreshInterval = null;
      }
    },
    async loadBadgeSummary() {
      try {
        this.badgeSummary = await callAPI(this.$store, "navigation/summary", {
          showLoader: false,
          showErrorNotification: false,
        }) || this.badgeSummary;
      } catch (error) {
        console.error("Failed to load navigation summary", error);
      }
    },
    withBadge(item) {
      const badge = this.badgeFor(item);
      return { ...item, badge };
    },
    badgeFor(item) {
      if (item.label === "uploads-my") {
        return this.badgeSummary.activeUploadCount || 0;
      }
      if (item.label === "downloads") {
        return this.badgeSummary.activeDownloadCount || 0;
      }
      if (item.label === "trash") {
        return this.badgeSummary.recoverableTrashRootCount || 0;
      }
      return 0;
    },
    badgeText(value) {
      return value > 99 ? "99+" : String(value);
    },
    isItemVisible(item) {
      if (item.label === 'browse-repositories' && !FEATURE_BROWSE_REPOSITORIES) {
        return false;
      }
      if (item.feature === 'MY_DOC_RETENTION' && !FEATURE_MY_DOCUMENT_RETENTION) {
        return false;
      }
      if (item.auth === AUTH.PERSONAL_STORAGE) {
        return this.hasPersonalStorage;
      } else if (item.auth === AUTH.USER) {
        return true;
      } else if (item.auth === AUTH.ADMIN) {
        return this.isAdmin;
      } else if (item.auth === AUTH.REDS) {
        return this.isRedsUser;
      } else if (item.auth === AUTH.MULTI_FILE_DOWNLOAD) {
        return true;
      }
      return false;
    },
  },
  watch: {
    isAuthenticated(authenticated) {
      if (authenticated) {
        this.startBadgePolling();
      } else {
        this.stopBadgePolling();
      }
    },
  },
};
</script>

<template>
  <nav>
    <div class="nav-scroll">
      <ul v-for="(group, groupIndex) in visibleGroups" :key="groupIndex" class="nav-group" :class="{ 'nav-group--divided': groupIndex > 0 }">
        <li v-for="item in group.items" :key="item.label">
          <router-link :to="item.url">
            <span v-if="item.icon" class="nav-icon">
              <span class="material-icons">{{item.icon}}</span>
            </span>
            <span class="nav-label">{{ $t(item.label) }}</span>
            <span v-if="item.badge" class="nav-badge">{{ badgeText(item.badge) }}</span>
          </router-link>
        </li>
      </ul>
      <ul v-if="visibleBottomItems.length" class="nav-bottom">
        <li v-for="item in visibleBottomItems" :key="item.label">
          <router-link :to="item.url">
            <span v-if="item.icon" class="nav-icon">
              <span class="material-icons">{{item.icon}}</span>
            </span>
            <span class="nav-label">{{ $t(item.label) }}</span>
            <span v-if="item.badge" class="nav-badge">{{ badgeText(item.badge) }}</span>
          </router-link>
        </li>
      </ul>
    </div>
    <div class="app-version">Version: {{ appVersion }}</div>
  </nav>
</template>

<style lang="scss" scoped>
nav {
  background: #002856;
  color: white;

  padding: 2.5rem 1rem 1rem 2rem;
  position: relative; /* Added for positioning the version */
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  height: 100%;
}

.nav-scroll {
  min-height: 0;
  overflow: auto;
  padding-bottom: 1rem;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

li {
  margin: 0 0 2rem;

  ul {
    margin-top: 2rem; // Increased spacing between parent and first child
    margin-left: 1rem; // Indent sub-menu
    li {
      margin-bottom: 2rem; // Same margin as top-level items
    }
  }
}

.nav-group--divided {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 1.5rem;
  padding-top: 1.5rem;
}

.nav-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 1.5rem;
  padding-top: 1.5rem;

  li {
    margin-bottom: 0;
  }
}

a:link, a:visited {
  align-items: center;
  color: white;
  text-decoration: none;
  display: grid;
  gap: 0.5rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
}

a:active, a:hover {
  color: white;
}

.nav-icon {
  display: inline-flex;
}

.material-icons {
  line-height: 1;
}

.nav-label {
  min-width: 0;
}

.nav-badge {
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #002856;
  border-radius: 999px;
  color: #002856;
  display: inline-flex;
  font-size: 0.65rem;
  font-weight: 700;
  justify-content: center;
  line-height: 1;
  min-width: 1rem;
  padding: 0.1rem 0.25rem;
}

.app-version {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 0.75rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5); /* Lighter color for watermark effect */
}
</style>
